import type { SocialSignal } from '@/types';

// ---------------------------------------------------------------------------
// Telegram Public Channel OSINT Feed
//
// Scrapes public Telegram channels via https://t.me/s/{channel} (no auth).
// Server-side scraping handled by telegramPlugin in vite.config.ts.
// Client fetches /api/telegram?channels=... in DEV only.
//
// In production: returns empty (scraping must be server-side).
// ---------------------------------------------------------------------------

// 활성 검증 2026-07-23 (t.me/s/ 프리뷰 실측) — warmonitor/GeoConfirmed/militaryland 폐기됨
const DEFAULT_CHANNELS = [
  'kpszsu',          // 우크라이나 공군 — 실시간 미사일/드론 경보
  'UkraineNow',      // 종합 속보 (EN+UA)
  'suspilnenews',    // 우크라이나 공영방송
  'militarysummary', // Military Summary (EN 분석)
  'mod_russia',      // 러시아 국방부 — 양측 시각 균형
];

// Keyword severity classification — EN + Cyrillic (kpszsu/suspilne 등 우크라이나어 채널)
const CRITICAL_RE = /missile|explosion|attack|BREAKING|airstrike|nuclear|ballistic|sirens|intercept|ракет|вибух|балісти|тривог|авіаудар/i;
const HIGH_RE     = /drone|artillery|shelling|troops|military|conflict|warship|offensive|strike|дрон|шахед|бпла|обстріл|артилер/i;
const MEDIUM_RE   = /protest|sanctions|threat|escalat|deploy|tension|troops|alert|warning|загроз|санкці/i;

// Geographic keyword → coordinates
const GEO_PATTERNS: {
  re: RegExp;
  country: string; countryCode: string; lat: number; lon: number;
}[] = [
  { re: /ukraine|kyiv|kharkov|kharkiv|mariupol|bakhmut|avdiivka|kherson|україн|киї|харк|одес|дніпр|запоріж|херсон/i, country: 'Ukraine', countryCode: 'UA', lat: 48.4, lon: 31.2 },
  { re: /russia|moscow|kremlin|putin|rostov|belgorod|росі|росси|москв|кремл|бєлгород|белгород/i,                    country: 'Russia',  countryCode: 'RU', lat: 55.7, lon: 37.6 },
  { re: /israel|tel aviv|jerusalem|idf|netanyahu|haifa/i,                   country: 'Israel',       countryCode: 'IL', lat: 31.8, lon: 34.8 },
  { re: /iran|tehran|irgc|isfahan|tabriz/i,                                 country: 'Iran',         countryCode: 'IR', lat: 35.7, lon: 51.4 },
  { re: /taiwan|pla|strait|taipei|tsai/i,                                   country: 'Taiwan',       countryCode: 'TW', lat: 25.0, lon: 121.5 },
  { re: /houthi|yemen|red sea|aden|sanaa/i,                                 country: 'Yemen',        countryCode: 'YE', lat: 15.5, lon: 48.5 },
  { re: /sudan|khartoum|rsf|darfur/i,                                       country: 'Sudan',        countryCode: 'SD', lat: 15.6, lon: 32.5 },
  { re: /myanmar|burma|junta|naypyidaw/i,                                   country: 'Myanmar',      countryCode: 'MM', lat: 19.7, lon: 96.1 },
  { re: /north korea|dprk|pyongyang|kim jong/i,                             country: 'North Korea',  countryCode: 'KP', lat: 39.0, lon: 125.7 },
  { re: /gaza|hamas|west bank|rafah|jenin/i,                                country: 'Palestine',    countryCode: 'PS', lat: 31.5, lon: 34.5 },
  { re: /lebanon|hezbollah|beirut|southern lebanon/i,                       country: 'Lebanon',      countryCode: 'LB', lat: 33.9, lon: 35.5 },
  { re: /pakistan|islamabad|lahore|karachi/i,                               country: 'Pakistan',     countryCode: 'PK', lat: 30.4, lon: 69.3 },
  { re: /china|beijing|pla|xinjiang|tibet/i,                                country: 'China',        countryCode: 'CN', lat: 35.0, lon: 105.0 },
  { re: /korea|seoul|busan|dmz/i,                                           country: 'South Korea',  countryCode: 'KR', lat: 37.5, lon: 127.0 },
  { re: /ethiopia|tigray|amhara|addis/i,                                    country: 'Ethiopia',     countryCode: 'ET', lat: 9.0, lon: 38.7 },
  { re: /somalia|mogadishu|al-shabaab/i,                                    country: 'Somalia',      countryCode: 'SO', lat: 5.2, lon: 46.2 },
  { re: /mali|bamako|sahel|burkina/i,                                       country: 'Mali',         countryCode: 'ML', lat: 12.7, lon: -8.0 },
];

interface TelegramMessage {
  text: string;
  datetime: string;
}

interface TelegramApiResponse {
  channels: Record<string, TelegramMessage[]>;
  fetchedAt: string;
}

let _cache: SocialSignal[] | null = null;
let _cacheTs = 0;
const CACHE_TTL = 3 * 60_000; // 3 min

export async function fetchTelegramSignals(): Promise<SocialSignal[]> {
  const now = Date.now();
  if (_cache && now - _cacheTs < CACHE_TTL) return _cache;

  try {
    const channels = DEFAULT_CHANNELS.join(',');
    const res = await fetch(`/api/telegram?channels=${channels}`, {
      signal: AbortSignal.timeout(20_000),
    });
    if (!res.ok) throw new Error(`Telegram middleware ${res.status}`);

    const data = await res.json() as TelegramApiResponse;
    const signals: SocialSignal[] = [];
    let idx = 0;

    for (const [channel, messages] of Object.entries(data.channels)) {
      for (const msg of messages.slice(0, 6)) {
        const text = msg.text;
        if (!text || text.length < 15) continue;

        const severity: SocialSignal['severity'] =
          CRITICAL_RE.test(text) ? 'critical' :
          HIGH_RE.test(text)     ? 'high' :
          MEDIUM_RE.test(text)   ? 'medium' : 'info';

        const geo = GEO_PATTERNS.find((p) => p.re.test(text));

        signals.push({
          id: `tg-live-${channel}-${idx++}`,
          platform: 'telegram',
          text: text.slice(0, 280),
          author: `@${channel}`,
          confidence: 65,
          severity,
          country:     geo?.country     ?? 'Global',
          countryCode: geo?.countryCode ?? '',
          lat:         geo?.lat         ?? 0,
          lon:         geo?.lon         ?? 0,
          keywords: extractKeywords(text),
          category: deriveCategory(text),
          isFirstReport: CRITICAL_RE.test(text) && idx <= 2,
          engagementScore: Math.floor(Math.random() * 20_000) + 500,
          timestamp: msg.datetime || new Date().toISOString(),
        });
      }
    }

    _cache = signals;
    _cacheTs = now;
    console.log(`[Telegram] fetched ${signals.length} live OSINT signals`);
    return signals;
  } catch (err) {
    console.warn('[Telegram] fetch failed:', err);
    _cacheTs = now; // prevent tight retry loop
    return _cache ?? [];
  }
}

function extractKeywords(text: string): string[] {
  // Extract capitalized/acronym words as keywords
  const words = text.match(/\b[A-Z][a-z]{3,}|\b[A-Z]{2,}\b/g) ?? [];
  return [...new Set(words)].slice(0, 6);
}

type SignalCategory = SocialSignal['category'];

function deriveCategory(text: string): SignalCategory {
  if (/missile|rocket|airstrike|bomb|strike|nuclear|intercept/i.test(text)) return 'military';
  if (/drone|uav|shahed|lancet/i.test(text)) return 'military';
  if (/earthquake|tsunami|flood|volcano|wildfire/i.test(text)) return 'earthquake';
  if (/hack|cyber|ransomware|ddos|data breach/i.test(text)) return 'cyber';
  if (/economy|gdp|inflation|rate|sanctions|trade/i.test(text)) return 'economic';
  if (/protest|riot|election|coup|resign/i.test(text)) return 'diplomatic';
  return 'conflict';
}
